<template>
  <div class="w-full mx-auto flex flex-1 items-center justify-center">
    <div class="w-full max-w-[520px] mx-auto">
      <h1
        class="font-interDisplay text-4xl md:text-[48px] lg:text-[64px] leading-130 font-semibold text-dark text-left"
      >
        {{ $t("enter_system") }}
      </h1>

      <div class="mt-4 md:mt-8 space-y-2" dir="ltr">
        <SInput
          :error="form.$v.value.email.$error"
          v-model.trim="form.values.email"
          :placeholder="$t('enter_auth_email')"
          prefix-class="text-2xl text-gray-450 font-semibold flex items-center mr-3 leading-130"
        >
          <template #prefix>
            <i class="icon-email" />
          </template>
        </SInput>
        <div class="">
          <SFormGroup :for-text="form.values.password">
            <SInput
              v-model.trim="form.values.password"
              :error="form.$v.value.password.$error"
              :placeholder="$t('enter_password')"
              :type="isPassword ? 'password' : 'text'"
              :id="form.values.password"
              prefix-class="text-2xl text-gray-450 font-semibold flex items-center mr-3 leading-130"
            >
              <template #prefix>
                <i class="icon-password" />
              </template>
              <template #suffix>
                <SEyeToggle
                  class="translate-y-0.5"
                  :type-password="!isPassword"
                  @click="isPassword = !isPassword"
                  hover-color="#333"
                />
              </template>
            </SInput>
            <div class="flex-y-center justify-end mt-1 mb-10">
              <RouterLink
                to="/auth/reset-password"
                class="w-auto text-right text-green hover:text-green-400 transition-300 text-sm cursor-pointer"
              >
                {{ $t("forget_password") }}
              </RouterLink>
            </div>
          </SFormGroup>
        </div>
        <vue-recaptcha
          class="mt-5 !mb-5 mx-auto"
          ref="recaptcha"
          :sitekey="siteKey"
          @verify="verifyMethod"
          @expired="expiredMethod"
          @error="onError"
        />
        <SButton
          class="w-full"
          :text="$t('login')"
          @click="submit"
          v-bind="{ loading }"
          :disabled="!captchaToken"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useForm } from "@/composables/useForm";
import { email, required } from "@vuelidate/validators";
import SButton from "@/components/Common/CButton.vue";
import { ref, onMounted } from "vue";
import { VueRecaptcha } from "vue-recaptcha";
import SFormGroup from "@/components/Form/FGroup.vue";
import SInput from "@/components/Form/Input/FInput.vue";
import SEyeToggle from "@/components/Form/Input/CEyeToggle.vue";
import { useI18n } from "vue-i18n";
import { useCustomToast } from "@/composables/useCustomToast";
import { useAuthStore } from "@/modules/Auth/stores";
import { JwtService } from "@/services/JwtService";
import apiService from "@/services/ApiService";
import { useRouter } from "vue-router";
import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
import { useLocalizedNavigation } from "@/composables/useLocalizedNavigation";
import { trackCustomEvent } from "@/utils/fbq";

const store = useAuthStore();

const router = useRouter();

const siteKey = import.meta.env.VITE_APP_SITE_KEY;
const { t } = useI18n();
const { showToast } = useCustomToast();
const { pushToDataLayer } = usePushToDataLayer();
const { navigateTo } = useLocalizedNavigation();

const captchaToken = ref();
const loading = ref(false);
const isPassword = ref(true);
const rememberMe = ref(false);

const form = useForm(
  {
    email: "",
    password: "",
  },
  {
    email: {
      required,
      email,
    },
    password: {
      required,
    },
  },
);

async function submit() {
  form.$v.value.$touch();

  trackCustomEvent("UserLoginAttempt", {
    event_category: "email",
    event_label: "login_attempt",
    button_text: "Login",
  });
  // Track login attempt
  pushToDataLayer("login_attempt", {
    email: form.values.email?.toLowerCase(),
    hasPassword: !!form.values.password,
    hasCaptcha: !!captchaToken?.value,
    event_category: "email",
    event_label: "login_attempt",
    button_text: "Login",
  });

  if (captchaToken?.value) {
    if (!form.$v.value.$invalid) {
      loading.value = true;
      const data = {
        email: form.values.email?.toLowerCase(),
        password: form.values.password,
      };
      await store
        .login(data)
        .then(async (res: any) => {
          JwtService.saveToken(res?.data?.tokens?.access);
          JwtService.saveRefreshToken(res?.data?.tokens?.refresh);
          apiService.setHeader();
          await store.fetchUserData();
          await handleRememberUser();
          await navigateTo({ name: "PDashboard" });
          showToast(t("successfully_entered"), "success");

          // Track successful login event
          trackCustomEvent("UserLoginSuccess", {
            event_category: "email",
            event_label: "login_success",
            button_text: "Login",
          });
          pushToDataLayer("login_success", {
            email: data.email,
            event_category: "email",
            event_label: "login_success",
            button_text: "Login",
          });
        })
        .catch((err) => {
          if (err?.response?.status === 429) {
            navigateTo({ name: "PAuthBlocked" });
            store.blockedTime = +err?.response?.headers["retry-after"];

            // Track blocked account
            pushToDataLayer("login_blocked", {
              email: data.email,
              retryAfter: store.blockedTime,
              event_category: "blocked",
              event_label: "login_blocked",
              button_text: "Login",
            });
          } else if (err?.response?.status === 500) {
            showToast(t("some_error"), "error");

            // Track server error
            pushToDataLayer("login_server_error", {
              email: data.email,
              event_category: "server",
              event_label: "login_server_error",
              button_text: "Login",
            });
          } else {
            const errorMessage =
              err?.response?.data?.[0]?.error?.message ||
              t("check_login_password");
            showToast(errorMessage, "error");

            // Track authentication error
            pushToDataLayer("login_auth_error", {
              email: data.email,
              errorType: err?.response?.status || "unknown",
              event_category: "email",
              event_label: "login_auth_error",
              button_text: "Login",
            });
          }
          loading.value = false;
        })
        .finally(() => {
          loading.value = false;
        });
    } else {
      // Track validation error
      pushToDataLayer("login_validation_error", {
        email: form.values.email?.toLowerCase(),
        hasEmailError: form.$v.value.email.$error,
        hasPasswordError: form.$v.value.password.$error,
        event_category: "validation",
        event_label: "login_validation_error",
        button_text: "Login",
      });
    }
  } else {
    // Track missing captcha
    pushToDataLayer("login_missing_captcha", {
      email: form.values.email?.toLowerCase(),
      event_category: "captcha",
      event_label: "login_missing_captcha",
      button_text: "Login",
    });
  }
}

function verifyMethod(response: string) {
  captchaToken.value = response;

  // Track successful captcha verification
  pushToDataLayer("captcha_verified", {
    email: form.values.email?.toLowerCase(),
    event_category: "captcha",
    event_label: "captcha_verified",
    button_text: "Captcha",
  });
}

function expiredMethod() {
  captchaToken.value = null;

  // Track captcha expiration
  pushToDataLayer("captcha_expired", {
    email: form.values.email?.toLowerCase(),
    event_category: "captcha",
    event_label: "captcha_expired",
    button_text: "Captcha",
  });
}

function onError(error: Error) {
  console.error("ReCAPTCHA error:", error);

  // Track captcha error
  pushToDataLayer("captcha_error", {
    email: form.values.email?.toLowerCase(),
    errorMessage: error?.message || "Unknown reCAPTCHA error",
    event_category: "captcha",
    event_label: "captcha_error",
    button_text: "Captcha",
  });

  throw new Error(error?.message || "Unknown reCAPTCHA error");
}

const handleRememberUser = () => {
  const user = {
    name: form.values.email,
    password: form.values.password,
  };
  if (rememberMe.value && !localStorage.getItem("user")) {
    localStorage.setItem("user", JSON.stringify(user));
  } else {
    localStorage.removeItem("user");
  }
};
const checkUser = () => {
  if (localStorage.getItem("user")) {
    const storage = localStorage.getItem("user");
    const user = {
      name: JSON.parse(storage)?.name,
      password: JSON.parse(storage)?.password,
    };
    form.values.password = user?.password;
    form.values.email = user?.name;
    form.values.rememberMe = true;
  }
};
onMounted(() => {
  checkUser();
  pushToDataLayer("pageview", {
    page: "login",
    event_category: "page",
    event_label: "login_page_view",
    button_text: "None",
  });
});
</script>
